import { Component, Injector, ViewChild, ElementRef, NgZone, inject, ChangeDetectionStrategy } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService, NzNotificationDataOptions } from 'ng-zorro-antd/notification';
import { NzImageService } from 'ng-zorro-antd/image';

import { AppBaseFormComponent } from './app-base-form.component';

import { IconDictionaryService } from '@services/icon-dictionary.service';
import { FormOperationService } from '@core/services/form-manager.service';
import { RouteReuseService } from '@services/route-reuse.service';

import {
  StandardModalComponent,
  UploadModalComponent,
  ImageUploadModalComponent,
} from '@components/modals/standard-modals.component';

@Component({
  standalone: true,
  selector: 'app-form',
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
})
export class AppFormComponent extends AppBaseFormComponent {
  protected _iconDictionary: IconDictionaryService;
  protected _formOperation: FormOperationService;
  protected _modalService: NzModalService;
  protected _notificationService: NzNotificationService;
  protected _message: NzMessageService;
  protected _nzImageService: NzImageService;
  protected _routeReuseService: RouteReuseService;
  protected _markedRoute: string | null = null;
  protected _ngZone: NgZone;

  /**
   * Elementos del formulario
   */
  @ViewChild('formContainer') set formContainer(ref: ElementRef<HTMLDivElement>) {
    this._formContainer = ref;
    this.setupResizeObserver();
  }
  get formContainer(): ElementRef<HTMLDivElement> {
    return this._formContainer;
  }
  protected _formContainer!: ElementRef<HTMLDivElement>;

  /**
   * Observación responsiva del contenedor del formulario.
   * Cuando el ancho del contenedor baja del breakpoint se considera "compacto"
   * y se invoca onCompactChange() para que los formularios hijos ajusten su layout.
   */
  protected resizeObserver?: ResizeObserver;
  protected readonly COMPACT_BREAKPOINT: number = 768;
  isCompact = false;

  constructor() {
    const injector = inject(Injector);

    super();
    this._iconDictionary = injector.get(IconDictionaryService);
    this._formOperation = injector.get(FormOperationService);
    this._modalService = injector.get(NzModalService);
    this._notificationService = injector.get(NzNotificationService);
    this._message = injector.get(NzMessageService);
    this._nzImageService = injector.get(NzImageService);
    this._routeReuseService = injector.get(RouteReuseService);
    this._ngZone = injector.get(NgZone);
  }

  override start() {
    super.start();
    this.enabledSections = this.visibleSections.filter(sec => sec.getCustomAttribute('displayType') !== 'modal');
  }

  override ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    super.ngOnDestroy();
  }

  /**
   * Crea (o recrea) el ResizeObserver sobre el contenedor del formulario.
   * Se reinvoca cada vez que cambia la referencia del @ViewChild.
   */
  private setupResizeObserver() {
    this.resizeObserver?.disconnect();
    const el = this._formContainer?.nativeElement;
    if (!el) {
      return;
    }
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width ?? Infinity;
      const compact = width < this.COMPACT_BREAKPOINT;
      if (compact !== this.isCompact) {
        // El callback de ResizeObserver corre fuera de la zona de Angular,
        // así que re-entramos a la zona para disparar change detection.
        // Va dentro del guard: solo se ejecuta al cruzar el breakpoint, no en cada resize.
        this._ngZone.run(() => {
          this.isCompact = compact;
          this.onCompactChange(compact);
        });
      }
    });
    this.resizeObserver.observe(el);
  }

  /**
   * Se invoca cuando el contenedor del formulario cruza el breakpoint compacto.
   * Sobreescribir en el componente hijo para ajustar layout o configuración
   * de elementos y mejorar la visualización en anchos reducidos.
   */
  onCompactChange(_compact: boolean): void {
    // Implementación por defecto vacía: los formularios hijos la sobrecargan.
  }

  /**
   * Marca la ruta actual para que se preserve su estado al navegar fuera.
   * Llamar antes de navegar a otro componente (ej: validación de identidad)
   * para que al regresar el formulario conserve su estado.
   * Al regresar se invoca automáticamente onReturnFromReuse().
   */
  markForReuse(): void {
    const currentPath = this._router.url.split('?')[0].substring(1);
    this._markedRoute = currentPath;
    this._routeReuseService.markForReuse(currentPath);
    this._routeReuseService.onReattach(currentPath, () => {
      this.onReturnFromReuse();
    });
  }

  /**
   * Desmarca la ruta para que vuelva a su ciclo de vida normal (se destruya al salir).
   * Llamar cuando se regresa al formulario y ya no se necesita preservar el estado.
   */
  unmarkForReuse(): void {
    const path = this._markedRoute || this._router.url.split('?')[0].substring(1);
    this._routeReuseService.unmarkForReuse(path);
    this._markedRoute = null;
  }

  /**
   * Se invoca automáticamente cuando el formulario es restaurado después de markForReuse().
   * Sobreescribir en el componente hijo para manejar el regreso
   * (ej: consultar resultado de validación desde storage).
   */
  onReturnFromReuse(): void {
    // Diferir la limpieza para que Angular termine el proceso de reattach
    // antes de que borremos el handle del storage
    setTimeout(() => this.unmarkForReuse(), 0);
  }

  setTabType(tabType: string) {
    // card | line
    this.tabType = tabType;
  }

  setTabPosition(tabPosition: string) {
    this.tabPosition = tabPosition;
  }

  setHorizontal(layout: string) {
    this.layout = layout;
  }

  override displayActionServerError() {
    if (this._showServerErrorPopup) {
      this.showTitledMessage(
        this.errorMessage,
        this.errorDetail.slice(0, 100),
        '',
        (this.formConfig as any).errorTypes.error
      );
    }
  }

  override displayValidationServerError() {
    // 'success' 'info' 'warning' 'error'
    const notificationType = 'error';
    this._notificationService.create(notificationType, this.errorMessage, this.errorDetail);
  }

  override displayTableServerError() {
    this.showTitledMessage(this.errorMessage, this.errorDetail, '', (this.formConfig as any).errorTypes.error);
  }

  async showInformationModal(type, title, body, options = null) {
    /**
     * type: info, success, error, warning,
     * options permite soportar configuraciones adicionales como textos, callback y demás
     */
    let actionText = 'Aceptar';
    const modalFunction = this._modalService?.[type] ?? this._modalService?.info;
    const additionalOptions: any = {};
    if (options) {
      // En la medida de mas opciones, se incluyen aquí:
      const { callback, okButtonText } = options;
      okButtonText && (actionText = okButtonText);
      callback && (additionalOptions.nzOnOk = callback);
    }
    const modalObject: any = { nzTitle: title, nzContent: body, nzOkText: actionText, ...additionalOptions };
    modalFunction(modalObject);
  }

  protected openModal(title, body, options, contentComponent, callback?, parameters?) {
    const params = { title, body, options, ...parameters };
    params.body = body || params?.body;
    params.options = params.options ?? ['Aceptar'];
    const autoClose = params?.autoClose ?? true;

    const modalName = uuidv4();
    this.modalDialogs[modalName]?.close();
    this.modalDialogs[modalName] = null;
    const footerOptions = [];
    for (let index = 0; index < params.options.length; index++) {
      const option = params.options[index];
      const footerOption = typeof option === 'string' ? { label: option } : { ...option };
      footerOption.type = footerOption.type ?? 'primary';
      footerOption.disabled = footerOption.disabled ?? false;
      footerOption.onClick = () => callback?.(index, modalName, autoClose);
      footerOptions.push(footerOption);
    }
    const disableClose = params.disableClose ?? false;
    const modalInstance: NzModalRef = this._modalService.create({
      nzTitle: params.title,
      nzContent: contentComponent,
      nzFooter: footerOptions,
      nzMaskClosable: !disableClose,
      nzClosable: !disableClose,
      ...(params.onCancel ? { nzOnCancel: params.onCancel } : {}),
    });
    this.modalDialogs[modalName] = modalInstance;
    modalInstance?.getContentComponent()?.setContent(params);
    return modalName;
  }

  private closeModal(modalName) {
    this.modalDialogs[modalName]?.close();
    this.modalDialogs[modalName] = null;
  }

  override async showModalDialog(title, body, options, callback?, parameters?) {
    const params = { ...parameters };
    const autoClose = params?.autoClose ?? true;
    this.openModal(
      title,
      body,
      options,
      StandardModalComponent,
      (index, modalName) => {
        callback?.(index, this.modalDialogs[modalName]);
        if (autoClose) {
          this.closeModal(modalName);
        }
      },
      parameters
    );
  }

  override openUploadDialog(title, body, options, callback?, parameters?): void {
    const params = { ...parameters };
    const autoClose = params?.autoClose ?? true;
    this.openModal(
      title,
      body,
      options,
      UploadModalComponent,
      (index, modalName) => {
        this.modalDialogs[modalName]?.getContentComponent()?.getFiles(fileContentObj => {
          callback?.(index, fileContentObj, this.modalDialogs[modalName]);
          if (autoClose) {
            this.closeModal(modalName);
          }
        });
      },
      parameters
    );
  }

  openImageUploadDialog(title, body, options, callback?, parameters?): void {
    const params = { ...parameters };
    const autoClose = params?.autoClose ?? true;
    this.openModal(
      title,
      body,
      options,
      ImageUploadModalComponent,
      (index, modalName) => {
        this.modalDialogs[modalName]?.getContentComponent()?.getFiles(fileContentObj => {
          callback?.(index, fileContentObj, this.modalDialogs[modalName]);
          if (autoClose) {
            this.closeModal(modalName);
          }
        });
      },
      parameters
    );
  }

  override showFieldInfo(fieldCode: string, detail?: any): void {
    if (!detail) {
      const fieldInfo = this.getField(fieldCode)?.info;
      if (!fieldInfo) {
        return;
      }
      let infoTitle = '';
      let infoDetail = '';
      try {
        const info: any = structuredClone(fieldInfo);
        infoTitle = info.infoTitle ?? '';
        infoDetail = info.infoDetail ?? '';
      } catch {
        return;
      }
      this.showModalDialog(infoTitle, infoDetail, ['Cerrar'], null);
    }
  }

  async showMessage(message, type = 'success', options: any = {}) {
    message && this.showTitledMessage(message, '', type, options);
  }

  async showTitledMessage(title, message, type = 'success', options: any = {}) {
    const params = { ...options };
    const toastOptions: NzNotificationDataOptions = {
      nzDuration: params?.duration ?? 8000,
      nzPlacement: 'top',
      nzStyle: { 'font-weight': 'bold' },
      // nzKey: reusar la misma key actualiza el toast existente en vez de apilar duplicados.
      ...(params?.nzKey ? { nzKey: params.nzKey } : {}),
    };
    this._notificationService.create(type, title, message, toastOptions);
  }

  showNotifyPopup(title, message, type = 'success') {
    this.showTitledMessage(title, message, type);
  }

  showNotifyPopupAlert(title, message, type = 'success') {
    this.showTitledMessage(title, message, type);
  }

  async presentToast(
    message,
    _error = false,
    _icon = 'chevron-down-circle-sharp',
    _toasPosition = null,
    _duration = 5000
  ) {
    this.logger.debug('virtual presentToast');
  }

  imagePreview(image) {
    this._nzImageService.preview([{ src: image }], { nzZoom: 0.8, nzRotate: 0 });
  }

  print(pdf) {
    pdf
      ?.getData()
      .then(u8 => {
        const blob = new Blob([u8.buffer], {
          type: 'application/pdf',
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          iframe.focus();
          iframe.contentWindow.print();
        };
        this.hideSectionModalDialog();
      })
      .catch(err => {
        this.logger.error('[AppForm] Error al imprimir PDF:', err);
      });
  }

  /**
   * Implementación de scroll
   */

  scrollToTop() {
    try {
      if (this.formContainer?.nativeElement) {
        this.formContainer.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          document.querySelector('.card-content')?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 10);
      }
    } catch (e) {
      this.logger.error('scrollToTop error', e);
    }
  }
}
